import {
  createRootRouteWithContext,
  Outlet,
  redirect,
  type RegisteredRouter,
  type RouteIds,
} from '@tanstack/react-router'
import { type } from 'arktype'

import type { CamelToSnake } from '@orus.eu/backend'
import { groupCategorySchema, frenchSocialSecurityRegimeTypeSchema } from '@orus.eu/dimensions'
import { productSelectionDimensions } from '@orus.eu/dimensions-aggregate'
import { operatingZoneSchema } from '@orus.eu/operating-zone'
import { languages } from '@orus.eu/translations'

import { ChatWrapper } from '../components/templates/chat-wrapper.tsx'
import { OperatingZoneSync } from '../components/templates/operating-zone-wrapper.tsx'
import { SafeErrorBoundary } from '../components/templates/safe-error-boundary.tsx'
import { useTracking } from '../lib/tracking/tracking.ts'
import type { RouterContext } from '../shared/routing/router-context.ts'
import { isRedirectTargetAlreadySafe, sanitizeRedirectTarget } from '../shared/routing/sanitize-redirect-target.ts'

// Retrocompat only — maps legacy camelCase URL params to their snake_case equivalents.
// Do NOT add new entries here; all new query params must use snake_case from the start.
const LEGACY_TO_MODERN_MAP: Record<string, string> = {
  embeddingPartner: 'embedding_partner',
  embedInIframe: 'embed_in_iframe',
  stepId: 'step_id',
  continueSubscriptionId: 'continue_subscription_id',
  fromUnauthorizedCode: 'from_unauthorized_code',
  operatingZone: 'operating_zone',
  endorsementId: 'endorsement_id',
  notificationKey: 'notification_key',
  rcpaPersona: 'rcpa_persona',
  mrpSelected: 'mrp_selected',
  rcphSelected: 'rcph_selected',
  mrphSelected: 'mrph_selected',
  mrpwSelected: 'mrpw_selected',
  mutaSelected: 'muta_selected',
  rcdaSelected: 'rcda_selected',
  esRcphSelected: 'es_rcph_selected',
  nlRcphSelected: 'nl_rcph_selected',
  nlAovqlSelected: 'nl_aovql_selected',
  frPrelSelected: 'fr_prel_selected',
  frRcpwSelected: 'fr_rcpw_selected',
  frRccwSelected: 'fr_rccw_selected',
  rcpaSelected: 'rcpa_selected',
  mrpaSelected: 'mrpa_selected',
  rcpaxaSelected: 'rcpaxa_selected',
  esMrpwSelected: 'es_mrpw_selected',
  lastname: 'last_name',
  firstname: 'first_name',
}

type ProductSelectionSnakeKey = `${CamelToSnake<(typeof productSelectionDimensions)[number]['name']>}?`

// Product selection params — all snake_case. Do NOT add camelCase variants here.
const productSelectionFieldsSchema = type({
  'rcph_selected?': 'boolean | undefined',
  'mrph_selected?': 'boolean | undefined',
  'mrpw_selected?': 'boolean | undefined',
  'muta_selected?': 'boolean | undefined',
  'rcda_selected?': 'boolean | undefined',
  'es_rcph_selected?': 'boolean | undefined',
  'fr_prel_selected?': 'boolean | undefined',
  'nl_rcph_selected?': 'boolean | undefined',
  'nl_aovql_selected?': 'boolean | undefined',
  'fr_rcpw_selected?': 'boolean | undefined',
  'fr_rccw_selected?': 'boolean | undefined',
  'fr_aupa_selected?': 'boolean | undefined',
  'rcpa_selected?': 'boolean | undefined',
  'mrpa_selected?': 'boolean | undefined',
  'rcpaxa_selected?': 'boolean | undefined',
  'es_mrpw_selected?': 'boolean | undefined',
} as const satisfies Record<ProductSelectionSnakeKey, 'boolean | undefined'>)

const coercedString = type('unknown').pipe((data) => (data === undefined ? undefined : String(data)))

const searchParams = type({
  'discount_code?': 'string | undefined',
  'referral_code?': 'string | undefined',
  'embedding_partner?': 'string | undefined',
  'organization?': 'string | undefined',
  'partner_id?': coercedString,
  'activity?': 'string | undefined',
  'activity_name?': 'string | undefined',
  'has_existing_health_insurance?': 'boolean | undefined',
  'category?': 'string | undefined',
  'first_name?': coercedString,
  'last_name?': coercedString,
  'siret?': coercedString,
  'siren?': coercedString,
  'ape?': coercedString,
  'cif?': 'string | undefined',
  'nif?': 'string | undefined',
  'email?': 'string | undefined',
  'phone?': coercedString,
  'revenue?': coercedString,
  'redirect?': 'string | undefined',
  'token?': 'string | undefined',
  'login?': 'string | undefined',
  'arrives_receive_quote?': 'boolean | undefined',
  'redirect_status?': 'string | undefined',
  'embed_in_iframe?': 'boolean | undefined',
  'description?': 'string | undefined',
  'exclusion_name?': 'string | undefined',
  'step_id?': 'string | undefined',
  'validate?': 'boolean | undefined',
  'continue_subscription_id?': 'string | undefined',
  'from_unauthorized_code?': 'boolean | undefined',
  'discount?': 'string | undefined',
  'operating_zone?': operatingZoneSchema.or('undefined'),
  'language?': type('===', ...languages).or('undefined'),
  'q?': coercedString,
  'endorsement_id?': 'string | undefined',
  'tab_id?': 'string | undefined',
  'review_tab_id?': 'string | undefined',
  'evaluation_run_id?': 'string | undefined',
  'compare_run_id?': 'string | undefined',
  'timeline_id?': 'string | undefined',
  'thread_id?': 'string | undefined',
  'documents_tab_id?': groupCategorySchema.or('undefined'),
  'notification_key?': 'string | undefined',
  'state?': 'string | undefined',
  'code?': 'string | undefined',
  'oauth_done?': 'boolean | undefined',
  'error?': 'string | undefined',
  'sso?': type('===', 'qonto').or('undefined'),
  'rcpa_persona?': 'string | undefined',
  'mrp_selected?': 'boolean | undefined',
  'signature_date_min?': 'string | undefined',
  'signature_date_max?': 'string | undefined',
  'statuses?': 'string | undefined',
  'yearly_premium_min?': 'number | undefined',
  'yearly_premium_max?': 'number | undefined',
  'payment_recurrences?': 'string | undefined',
  'lifecycle_states?': 'string | undefined',
  'amount_min?': 'number | undefined',
  'amount_max?': 'number | undefined',
  'active_contracts_min?': 'number | undefined',
  'active_contracts_max?': 'number | undefined',
  'active_subscriptions_min?': 'number | undefined',
  'active_subscriptions_max?': 'number | undefined',
  'products?': 'string | undefined',
  'organizations?': 'string | undefined',
  'owners?': 'string | undefined',
  'inside_animators?': 'string | undefined',
  'onboarding_specialists?': 'string | undefined',
  'is_managed_by_orus?': 'string | undefined',
  'date_min?': 'string | undefined',
  'date_max?': 'string | undefined',
  'postcodes?': 'string | undefined',
  'excluded_activities?': 'string | undefined',
  'excluded_since?': 'string | undefined',
  'excluded_from?': 'string | undefined',
  'unpaid_since_days?': 'string | undefined',
  'formal_notice_sent?': 'string | undefined',
  'social_security_regime?': frenchSocialSecurityRegimeTypeSchema.or('undefined'),
  'utm_source?': 'string | undefined',
  'utm_campaign?': 'string | undefined',
}).and(productSelectionFieldsSchema)

export type SearchParams = typeof searchParams.infer

function normalizeSearchParams(raw: Record<string, unknown>): Record<string, unknown> {
  const normalized: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(raw)) {
    const normalizedKey = LEGACY_TO_MODERN_MAP[key] ?? key
    normalized[normalizedKey] = value
  }
  return normalized
}

// Wraps the schema with legacy-param normalization while keeping it an arktype schema
// so TanStack Router can correctly infer the search params type.
const validatedSearchParams = type('unknown').pipe(
  (raw) => normalizeSearchParams(raw as Record<string, unknown>),
  searchParams,
)

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: ({ location }) => {
    const rawSearch = new URLSearchParams(location.searchStr)
    const hasLegacyParam = Array.from(rawSearch.keys()).some((key) => Object.hasOwn(LEGACY_TO_MODERN_MAP, key))
    if (hasLegacyParam) {
      const normalizedParams = new URLSearchParams()
      for (const [key, value] of rawSearch.entries()) {
        normalizedParams.set(LEGACY_TO_MODERN_MAP[key] ?? key, value)
      }
      throw redirect({
        href: location.pathname + '?' + normalizedParams.toString(),
        replace: true,
      })
    }

    // Single runtime chokepoint for the redirect target: normalizes it to a same-origin relative path
    // and strips the transient params that cause redirect-loop accumulation
    // A same-origin absolute target with no transient params is left untouched
    const rawRedirect = rawSearch.get('redirect')
    if (rawRedirect !== null && !isRedirectTargetAlreadySafe(rawRedirect)) {
      const sanitized = sanitizeRedirectTarget(rawRedirect)
      if (sanitized === null) {
        rawSearch.delete('redirect')
      } else {
        rawSearch.set('redirect', sanitized)
      }
      const query = rawSearch.toString()
      throw redirect({ href: query ? location.pathname + '?' + query : location.pathname, replace: true })
    }
  },
  validateSearch: validatedSearchParams,
  component: function Root() {
    useTracking(TRACKING_IGNORED_ROUTE_IDS)

    return (
      <ChatWrapper>
        <OperatingZoneSync />
        <SafeErrorBoundary>
          <Outlet />
        </SafeErrorBoundary>
      </ChatWrapper>
    )
  },
})

const TRACKING_IGNORED_ROUTE_IDS = new Set<RouteIds<RegisteredRouter['routeTree']>>([
  '__root__',
  '/receive-quote',
  '/s/q/$token',
  '/s/c/$token',
  '/s/hf/$token',
  '/bo/troubleshooting/sessions/$sessionId',
  '/bo/subscriptions/',
])
